/*
 * search-overlay.js — S16 in-nav predictive search (display-only).
 *
 * Progressive enhancement over snippets/search-overlay.liquid: converts the
 * no-JS store link into a toggle button and drives the overlay states
 * (short-query hint / debounced spinner / results / no-results, with any
 * fetch or parse error falling back to no-results, matching the app's
 * catch -> empty behavior). Queries ONLY Shopify's native predictive-search
 * JSON endpoint under the locale root passed by the snippet; result rows
 * link to the product.url Shopify returns. All dynamic text is written via
 * textContent — nothing from the network is interpreted as HTML.
 */
(function () {
  'use strict';

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 300;
  var LIMIT = 8;

  function init(root) {
    var toggle = root.querySelector('[data-search-toggle]');
    var panel = root.querySelector('[data-search-panel]');
    var input = root.querySelector('[data-search-input]');
    var hint = root.querySelector('[data-search-hint]');
    var empty = root.querySelector('[data-search-empty]');
    var spinner = root.querySelector('[data-search-spinner]');
    var list = root.querySelector('[data-search-list]');
    if (!toggle || !panel || !input || !hint || !empty || !spinner || !list) { return; }

    var base = (root.getAttribute('data-root') || '/').replace(/\/+$/, '') + '/search/suggest.json';
    var timer = null;
    var requestSeq = 0;
    var open = false;

    function show(el) { el.removeAttribute('hidden'); }
    function hide(el) { el.setAttribute('hidden', ''); }

    // Defense-in-depth: only http(s)/relative URLs from the suggest payload
    // may reach href/src (blocks javascript:/data: schemes outright).
    function safeUrl(value) {
      if (typeof value !== 'string' || !value) { return ''; }
      try {
        var parsed = new URL(value, window.location.origin);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? value : '';
      } catch (error) {
        return '';
      }
    }

    function setState(state) {
      if (state === 'hint') { show(hint); } else { hide(hint); }
      if (state === 'empty') { show(empty); } else { hide(empty); }
      if (state === 'loading') { show(spinner); } else { hide(spinner); }
      if (state === 'results') { show(list); } else { hide(list); }
    }

    function setOpen(next) {
      open = next;
      toggle.setAttribute('aria-expanded', next ? 'true' : 'false');
      if (next) {
        show(panel);
        window.requestAnimationFrame(function () { input.focus(); });
      } else {
        hide(panel);
      }
    }

    function row(product) {
      var href = safeUrl(product.url);
      if (!href) { return null; }
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.className = 'search-overlay__result';
      a.href = href;

      var imageUrl = '';
      if (product.featured_image && typeof product.featured_image.url === 'string') {
        imageUrl = safeUrl(product.featured_image.url);
      } else if (typeof product.image === 'string') {
        imageUrl = safeUrl(product.image);
      }
      if (imageUrl) {
        var img = document.createElement('img');
        img.className = 'search-overlay__thumb';
        img.src = imageUrl;
        img.alt = '';
        img.width = 36;
        img.height = 36;
        img.loading = 'lazy';
        a.appendChild(img);
      } else {
        var ph = document.createElement('span');
        ph.className = 'search-overlay__thumb search-overlay__thumb--placeholder';
        ph.setAttribute('aria-hidden', 'true');
        a.appendChild(ph);
      }

      var meta = document.createElement('span');
      meta.className = 'search-overlay__result-meta';
      var title = document.createElement('span');
      title.className = 'search-overlay__result-title';
      title.textContent = product.title || '';
      meta.appendChild(title);
      var subParts = [];
      if (product.vendor) { subParts.push(product.vendor); }
      if (product.type) { subParts.push(product.type); }
      if (subParts.length) {
        var sub = document.createElement('span');
        sub.className = 'search-overlay__result-sub';
        sub.textContent = subParts.join(' · ');
        meta.appendChild(sub);
      }
      a.appendChild(meta);
      li.appendChild(a);
      return li;
    }

    function render(products) {
      list.textContent = '';
      var rendered = 0;
      for (var i = 0; i < products.length; i++) {
        var item = row(products[i]);
        if (item) { list.appendChild(item); rendered++; }
      }
      if (!rendered) { setState('empty'); return; }
      setState('results');
    }

    function search(query) {
      var seq = ++requestSeq;
      setState('loading');
      var url = base +
        '?q=' + encodeURIComponent(query) +
        '&resources[type]=product&resources[limit]=' + LIMIT;
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) { throw new Error('suggest_not_ok'); }
          return response.json();
        })
        .then(function (data) {
          if (seq !== requestSeq) { return; }
          var products =
            data && data.resources && data.resources.results &&
            Array.isArray(data.resources.results.products)
              ? data.resources.results.products
              : [];
          render(products);
        })
        .catch(function () {
          if (seq !== requestSeq) { return; }
          render([]);
        });
    }

    input.addEventListener('input', function () {
      if (timer) { clearTimeout(timer); }
      var query = input.value.trim();
      if (query.length < MIN_CHARS) {
        requestSeq++;
        setState('hint');
        return;
      }
      timer = setTimeout(function () { search(query); }, DEBOUNCE_MS);
    });

    // The server renders an honest plain link (no-JS fallback). Only once the
    // toggle behavior exists does it become a button to assistive tech.
    toggle.setAttribute('role', 'button');
    toggle.setAttribute('aria-haspopup', 'dialog');
    toggle.setAttribute('aria-expanded', 'false');

    toggle.addEventListener('click', function (event) {
      event.preventDefault();
      setOpen(!open);
    });

    toggle.addEventListener('keydown', function (event) {
      if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        setOpen(!open);
      }
    });

    document.addEventListener('pointerdown', function (event) {
      if (open && !root.contains(event.target)) { setOpen(false); }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && open) {
        setOpen(false);
        toggle.focus();
      }
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    });

    setState('hint');
  }

  function boot() {
    var roots = document.querySelectorAll('[data-search-overlay]');
    for (var i = 0; i < roots.length; i++) { init(roots[i]); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
